/**
 * Theme runtime initialization
 * Reads persisted UI preferences and applies the theme mode.
 */

import { getUIPrefs } from '@services/storage';
import { applyThemeTokens, getDefaultTheme, setThemeMode } from '@infra/theme';
import { brand } from '@brand';
import type { ThemeDefinition, ThemeMode } from '@infra/theme';

export interface ThemeRuntimeOptions {
  root?: HTMLElement;
  theme?: ThemeDefinition;
  applyTokens?: boolean;
}

export function resolveThemeMode(preferred?: ThemeMode, supportsDarkMode = true): ThemeMode {
  if (!supportsDarkMode) return 'light';
  if (preferred === 'light' || preferred === 'dark') {
    return preferred;
  }

  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  return 'light';
}

export function initThemeRuntime(options: ThemeRuntimeOptions = {}): void {
  const prefs = getUIPrefs();
  const mode = resolveThemeMode(prefs?.theme, brand.supportsDarkMode);
  const root = options.root;

  if (options.applyTokens) {
    applyThemeTokens(options.theme ?? getDefaultTheme(mode), {
      root,
      mode,
      preserveModeClass: false,
    });
    return;
  }

  setThemeMode(
    mode,
    root ?? (typeof document !== 'undefined' ? document.documentElement : undefined)
  );
}
